
import React, { useEffect, useState } from 'react';
import { debugLog, debugWarn, debugError } from '@/utils/debugLogger';

interface OneSignalProviderProps {
  children: React.ReactNode;
}

// Singleton per prevenire inizializzazioni multiple
let oneSignalInitialized = false;
let initializationPromise: Promise<boolean> | null = null;

/**
 * OneSignalProvider ottimizzato per prevenire conflitti
 * 
 * Utilizza un singleton pattern per garantire una sola inizializzazione
 * e previene i conflitti con altre funzionalità dell'app.
 */
export const OneSignalProvider: React.FC<OneSignalProviderProps> = ({ children }) => {
  const [initState, setInitState] = useState<'idle' | 'initializing' | 'success' | 'failed'>('idle');

  useEffect(() => {
    // Skip initialization in SSR
    if (typeof window === 'undefined') {
      return;
    }

    // Use requestIdleCallback for better main thread scheduling
    // Falls back to setTimeout(2500ms) if requestIdleCallback is not available
    let cleanup: (() => void) | undefined;
    let isMounted = true;

    const doInit = () => {
      if (!isMounted) return;
      
      // Se già inizializzato, non fare nulla
      if (oneSignalInitialized) {
        setInitState('success');
        return;
      }

      // Se inizializzazione in corso, attendi
      if (initializationPromise) {
        initializationPromise.then(success => {
          if (isMounted) {
            setInitState(success ? 'success' : 'failed');
          }
        }).catch(() => {
          if (isMounted) {
            setInitState('failed');
          }
        });
        return;
      }

      // Avvia inizializzazione solo se necessario
      initializeOneSignal();
    };

    // requestIdleCallback schedules during browser idle time
    // This prevents blocking the main thread during critical render
    if ('requestIdleCallback' in window) {
      const idleId = (window as any).requestIdleCallback(doInit, { timeout: 5000 });
      cleanup = () => (window as any).cancelIdleCallback(idleId);
    } else {
      // Fallback to setTimeout for browsers without requestIdleCallback
      const timeoutId = setTimeout(doInit, 2500);
      cleanup = () => clearTimeout(timeoutId);
    }

    return () => {
      isMounted = false;
      cleanup?.();
    };
  }, []);

  const initializeOneSignal = async () => {
    if (oneSignalInitialized || initializationPromise) {
      return;
    }

    setInitState('initializing');
    debugLog('notifications', 'Starting OneSignal initialization with optimized sequence', undefined, 'OneSignalProvider');

    // Crea la promise di inizializzazione
    initializationPromise = new Promise(async (resolve) => {
      let earlyFallbackActivated = false;
      let timeoutId: NodeJS.Timeout;

      try {
        // Avviso a 15 secondi per attivazione fallback anticipata
        const earlyWarningTimeout = setTimeout(() => {
          debugWarn('notifications', 'Initialization taking longer than expected (15s), activating fallback...', undefined, 'OneSignalProvider');
          earlyFallbackActivated = true;
        }, 15000);

        // Inizializzazione solo SDK OneSignal (gestisce autonomamente il Service Worker)
        const success = await Promise.race([
          (async () => {
            debugLog('notifications', 'Initializing OneSignal SDK (manages its own Service Worker)...', undefined, 'OneSignalProvider');
            const { notificationService } = await import('@/services/notifications/notificationService');
            const sdkStart = performance.now();
            const sdkSuccess = await notificationService.init().catch((error) => {
              debugWarn('notifications', 'OneSignal SDK failed', error, 'OneSignalProvider');
              return false;
            });
            const sdkDuration = performance.now() - sdkStart;
            debugLog('notifications', `OneSignal SDK completed in ${sdkDuration.toFixed(2)}ms`, { success: sdkSuccess }, 'OneSignalProvider');
            
            return sdkSuccess;
          })(),
          new Promise<boolean>((_, reject) => {
            timeoutId = setTimeout(() => reject(new Error('Initialization timeout after 30s')), 30000);
          })
        ]);

        // Clear dei timeout
        clearTimeout(earlyWarningTimeout);
        clearTimeout(timeoutId);

        if (success) {
          oneSignalInitialized = true;
          const statusMsg = earlyFallbackActivated 
            ? "OneSignal initialized successfully (after fallback activation)"
            : "OneSignal initialized successfully";
          debugLog('notifications', statusMsg, undefined, 'OneSignalProvider');
          setInitState('success');
          resolve(true);
        } else {
          debugWarn('notifications', 'OneSignal initialization failed', undefined, 'OneSignalProvider');
          setInitState('failed');
          // Reset singleton per permettere nuovi tentativi
          oneSignalInitialized = false;
          initializationPromise = null;
          resolve(false);
        }
      } catch (error) {
        // Clear dei timeout in caso di errore
        clearTimeout(timeoutId);
        
        // Gestione migliorata per timeout - non mostrare come errore critico
        if (error instanceof Error && error.message.includes('timeout')) {
          const timeoutMsg = earlyFallbackActivated 
            ? "Initialization timeout (30s) - fallback was already active"
            : "Initialization timeout (30s) - fallback active";
          debugWarn('notifications', timeoutMsg, error.message, 'OneSignalProvider');
        } else {
          debugError('notifications', 'Initialization error', error, 'OneSignalProvider');
        }
        setInitState('failed');
        resolve(false);
      }
    });

    await initializationPromise;
  };

  // Cleanup dell'inizializzazione al unmount
  useEffect(() => {
    return () => {
      // Reset solo se il componente viene completamente smontato
      // Non durante i re-render normali
    };
  }, []);

  return <>{children}</>;
};
